"use client";

import { Bell, Bolt, ChevronDown, LogOut, Settings, UserCogIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import useModalState from "@/hooks/useModalState";
import LogoutModal from "../modal/LogoutModal";
import { useNavigate } from "react-router-dom";

const get2LetterInitials = (name: string) => {
  const [firstName, lastName] = name.split(" ");
  return `${firstName?.charAt(0)}${lastName?.charAt(0)}`;
};

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    profileUrl: string;
    role: string | { name: string } | null;
    group?: { name: string } | null;
    isAdmin?: boolean;
    leadershipRole?: string | null;
    isGroupLeader?: boolean;
  };
}) {
  const { isMobile } = useSidebar();
  const logoutModal = useModalState();
  const navigate = useNavigate();

  const isAdmin = (() => {
    if (user?.isAdmin === true) return true;
    const leadershipRole = user?.leadershipRole || (typeof user?.role === 'object' ? user.role?.name : null) || (typeof user?.role === 'string' ? user.role : null);
    const adminRoles = ["Technical Support", "President", "Secretary", "Accountant"];
    if (leadershipRole && adminRoles.includes(leadershipRole)) return true;
    if (user?.isGroupLeader === true) {
      const validRoles = ["President", "Secretary", "Accountant", "Admin", "Technical Support"];
      return leadershipRole && validRoles.includes(leadershipRole);
    }
    return false;
  })();

  const getRoleDisplayName = () => {
    const leadershipRole =
      user?.leadershipRole ||
      (typeof user?.role === "object" ? user.role?.name : null) ||
      (typeof user?.role === "string" ? user.role : null);

    if (leadershipRole && ["Technical Support", "President", "Secretary", "Accountant"].includes(leadershipRole)) {
      const groupName = user.group?.name;
      if (["President", "Secretary", "Accountant"].includes(leadershipRole)) {
        return groupName ? `${leadershipRole} (${groupName})` : leadershipRole;
      }
      return leadershipRole;
    }

    if (isAdmin) {
      return "Case d'Entraide";
    }

    return leadershipRole || "Member";
  };

  const roleDisplayName = getRoleDisplayName();
  const initials = user.name ? get2LetterInitials(user.name) : "M";

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="flex h-auto min-w-0 items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[#F8FAFC] data-[state=open]:bg-[#F8FAFC]"
              >
                <div className="relative shrink-0">
                  <Avatar className="size-9 rounded-full ring-2 ring-[#E5E7EB]">
                    <AvatarImage src={user.profileUrl || getDefaultAvatar(user.email || user.name)} alt={user.name} />
                    <AvatarFallback className="rounded-full bg-[#0A9F43] text-xs font-bold text-white uppercase">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-white bg-[#0A9F43]" />
                </div>
                <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                  <span className="truncate text-[14px] font-semibold text-[#111827]" title={user.name}>
                    {user.name}
                  </span>
                  <span className="truncate text-xs font-medium text-[#6B7280]" title={roleDisplayName}>
                    {roleDisplayName}
                  </span>
                </div>
                <ChevronDown className="size-4 shrink-0 text-[#9CA3AF]" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl border border-[#E5E7EB] p-1.5 shadow-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={6}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-3 px-2 py-2 text-left text-sm">
                  <Avatar className="size-9 rounded-full ring-2 ring-[#E5E7EB]">
                    <AvatarImage src={user.profileUrl || getDefaultAvatar(user.email || user.name)} alt={user.name} />
                    <AvatarFallback className="rounded-full bg-[#0A9F43] text-xs font-bold text-white uppercase">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate text-[14px] font-semibold text-[#111827]">{user.name}</span>
                    <span className="truncate text-xs font-medium text-[#6B7280]">
                      {roleDisplayName}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="mx-1 my-1" />

              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => navigate("/settings/account-settings")}
                  className="cursor-pointer rounded-lg px-2 py-2 text-[14px] text-[#374151] transition-colors hover:bg-[#F8FAFC]"
                >
                  <UserCogIcon size={18} className="mr-3 text-[#6B7280]" />
                  Account Settings
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/general-information")}
                    className="cursor-pointer rounded-lg px-2 py-2 text-[14px] text-[#374151] transition-colors hover:bg-[#F8FAFC]"
                  >
                    <Settings size={18} className="mr-3 text-[#6B7280]" />
                    System Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => navigate("/member/notifications")}
                  className="cursor-pointer rounded-lg px-2 py-2 text-[14px] text-[#374151] transition-colors hover:bg-[#F8FAFC]"
                >
                  <Bell size={18} className="mr-3 text-[#6B7280]" />
                  Notifications
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="mx-1 my-1" />
              <DropdownMenuItem
                onClick={() => logoutModal.open()}
                className="cursor-pointer rounded-lg px-2 py-2 text-[14px] text-[#374151] transition-colors hover:bg-[#FEF2F2] hover:text-[#DC2626]"
              >
                <LogOut size={18} className="mr-3 text-[#6B7280]" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <LogoutModal open={logoutModal.isOpen} onClose={logoutModal.close} />
    </>
  );
}
